import type {
    ChangeEvent,
    CSSProperties,
    FC,
    KeyboardEvent,
    ReactNode
} from 'react'
import type { ConsortiumCategory, ConsortiumSlug, SimulationSelection } from '@/types'

import { useEffect, useId, useMemo, useState } from 'react'
import { ArrowRight, Info, Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { clampCredit, simulateConsortium } from '@/lib/consortium'
import { buttonClassName } from '@/components/generals/button'
import { ConsortiumIcon } from '@/components/landing/consortium-icon'
import { consortiumCategories, getConsortiumCategory } from '@/constants/consorcios'

import {
    formatCompactCurrency,
    formatCurrency,
    formatNumber,
    formatPercent,
    maskCurrencyInput,
    onlyDigits
} from '@/lib/format'

interface ConsortiumSimulatorProps {
    initialSlug?: ConsortiumSlug
    onRequestProposal: (selection: SimulationSelection) => void
}

interface ComparisonBarProps {
    label: string
    detail?: string
    value: number
    max: number
    fillClassName: string
}

interface BreakdownRowProps {
    label: string
    value: string
    emphasis?: boolean
}

const optionClassName = 'inline-flex cursor-pointer select-none items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-gold'

const optionSelectedClassName = 'border-gold bg-gold text-gold-foreground'

const optionIdleClassName = 'border-ink-line bg-ink-soft text-primary-foreground/80 hover:border-primary-foreground/35 hover:text-primary-foreground'

const stepperClassName = 'grid size-12 shrink-0 cursor-pointer place-items-center rounded-full border border-ink-line text-primary-foreground transition-colors hover:border-primary-foreground/40 hover:bg-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent'

const ComparisonBar: FC<ComparisonBarProps> = ({ label, detail, value, max, fillClassName }): ReactNode => {
    const width = Math.max(4, Math.min(100, (value / max) * 100))

    return (
        <div>
            <div className='flex items-start justify-between gap-4 text-sm'>
                <p>
                    <span className='font-semibold text-foreground'>
                        {label}
                    </span>

                    {detail && (
                        <span className='mt-0.5 block text-xs text-muted-foreground'>
                            {detail}
                        </span>
                    )}
                </p>

                <p className='font-bold tabular-nums text-foreground'>
                    {formatCompactCurrency(value)}
                </p>
            </div>

            <div aria-hidden='true' className='mt-2 h-2.5 overflow-hidden rounded-[4px] bg-muted'>
                <div
                    className={cn('h-full rounded-r-[4px] transition-[width] duration-500 ease-out motion-reduce:transition-none', fillClassName)}
                    style={{ width: `${width}%` }}
                />
            </div>
        </div>
    )
}

const BreakdownRow: FC<BreakdownRowProps> = ({ label, value, emphasis = false }): ReactNode => {
    return (
        <div className='flex items-center justify-between gap-4 py-3'>
            <dt className={cn(emphasis ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
                {label}
            </dt>

            <dd className={cn('tabular-nums', emphasis ? 'font-bold text-foreground' : 'font-semibold text-foreground')}>
                {value}
            </dd>
        </div>
    )
}

export const ConsortiumSimulator: FC<ConsortiumSimulatorProps> = ({ initialSlug, onRequestProposal }): ReactNode => {
    const baseId = useId()
    const initialCategory = (initialSlug && getConsortiumCategory(initialSlug)) || consortiumCategories[0]

    const [slug, setSlug] = useState<ConsortiumSlug>(initialCategory.slug)
    const [credit, setCredit] = useState(initialCategory.credit.initial)
    const [creditDraft, setCreditDraft] = useState(formatNumber(initialCategory.credit.initial))
    const [term, setTerm] = useState(initialCategory.initialTerm)
    const [hasInteracted, setHasInteracted] = useState(false)
    const [announcement, setAnnouncement] = useState('')

    const category = getConsortiumCategory(slug) ?? consortiumCategories[0]
    const simulation = useMemo(() => simulateConsortium(category, credit, term), [category, credit, term])
    const { min, max, step } = category.credit
    const progress = ((credit - min) / (max - min)) * 100

    useEffect(() => {
        if (!hasInteracted) return

        const timeout = window.setTimeout(() => {
            setAnnouncement(`Parcela estimada de ${formatCurrency(simulation.installment, { cents: true })} por mês, em ${simulation.term} meses.`)
        }, 700)

        return () => window.clearTimeout(timeout)
    }, [hasInteracted, simulation.installment, simulation.term])

    const selectCategory = (next: ConsortiumCategory): void => {
        setHasInteracted(true)
        setSlug(next.slug)
        setCredit(next.credit.initial)
        setCreditDraft(formatNumber(next.credit.initial))
        setTerm(next.initialTerm)
    }

    const changeCredit = (value: number): void => {
        const next = clampCredit(category, value)

        setHasInteracted(true)
        setCredit(next)
        setCreditDraft(formatNumber(next))
    }

    const onDraftChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const masked = maskCurrencyInput(event.target.value)
        const value = Number(onlyDigits(masked))

        setCreditDraft(masked)

        if (value >= min && value <= max) {
            setHasInteracted(true)
            setCredit(value)
        }
    }

    const commitDraft = (): void => {
        const value = Number(onlyDigits(creditDraft))

        changeCredit(value > 0 ? value : credit)
    }

    const onDraftKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') event.currentTarget.blur()
    }

    const selectTerm = (value: number): void => {
        setHasInteracted(true)
        setTerm(value)
    }

    return (
        <div className='grid items-start gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,30rem)] lg:gap-16'>
            <div className='text-primary-foreground'>
                <fieldset>
                    <legend className='text-sm font-semibold text-primary-foreground/80'>
                        1. Tipo de consórcio
                    </legend>

                    <div className='mt-4 flex flex-wrap gap-2'>
                        {consortiumCategories.map(item => {
                            const isSelected = item.slug === slug

                            return (
                                <label
                                    key={item.slug}
                                    className={cn(optionClassName, isSelected ? optionSelectedClassName : optionIdleClassName)}
                                >
                                    <input
                                        type='radio'
                                        name={`${baseId}-tipo`}
                                        value={item.slug}
                                        checked={isSelected}
                                        onChange={() => selectCategory(item)}
                                        className='sr-only'
                                    />

                                    <ConsortiumIcon slug={item.slug} className='size-4' />
                                    {item.name}
                                </label>
                            )
                        })}
                    </div>
                </fieldset>

                <div className='mt-11'>
                    <div className='flex flex-wrap items-end justify-between gap-2'>
                        <label htmlFor={`${baseId}-valor`} className='text-sm font-semibold text-primary-foreground/80'>
                            2. Valor do crédito
                        </label>

                        <p className='text-xs text-primary-foreground/60'>
                            De {formatCompactCurrency(min)} a {formatCompactCurrency(max)}
                        </p>
                    </div>

                    <div className='mt-4 flex items-center gap-3'>
                        <button
                            type='button'
                            onClick={() => changeCredit(credit - step)}
                            disabled={credit <= min}
                            className={stepperClassName}
                        >
                            <Minus aria-hidden='true' className='size-5' />
                            <span className='sr-only'>
                                Diminuir {formatCurrency(step)}
                            </span>
                        </button>

                        <div className='flex min-w-0 flex-1 items-baseline gap-2 rounded-2xl border border-ink-line bg-ink-soft px-4 py-2.5 transition-colors focus-within:border-gold focus-within:ring-2 focus-within:ring-gold/30'>
                            <span aria-hidden='true' className='font-heading text-2xl text-gold sm:text-3xl'>
                                R$
                            </span>

                            <input
                                id={`${baseId}-valor`}
                                type='text'
                                inputMode='numeric'
                                autoComplete='off'
                                value={creditDraft}
                                onChange={onDraftChange}
                                onBlur={commitDraft}
                                onKeyDown={onDraftKeyDown}
                                aria-describedby={`${baseId}-valor-ajuda`}
                                className='w-full min-w-0 bg-transparent font-heading text-4xl leading-tight tabular-nums text-primary-foreground outline-none sm:text-5xl'
                            />
                        </div>

                        <button
                            type='button'
                            onClick={() => changeCredit(credit + step)}
                            disabled={credit >= max}
                            className={stepperClassName}
                        >
                            <Plus aria-hidden='true' className='size-5' />
                            <span className='sr-only'>
                                Aumentar {formatCurrency(step)}
                            </span>
                        </button>
                    </div>

                    <p id={`${baseId}-valor-ajuda`} className='sr-only'>
                        Digite um valor entre {formatCurrency(min)} e {formatCurrency(max)}.
                    </p>

                    <input
                        type='range'
                        min={min}
                        max={max}
                        step={step}
                        value={credit}
                        onChange={event => changeCredit(Number(event.target.value))}
                        aria-label='Valor do crédito'
                        aria-valuetext={formatCurrency(credit)}
                        className='range-input mt-6'
                        style={{ '--range-progress': `${progress}%` } as CSSProperties}
                    />

                    <div aria-hidden='true' className='mt-1 flex justify-between text-xs text-primary-foreground/55'>
                        <span>
                            {formatCompactCurrency(min)}
                        </span>

                        <span>
                            {formatCompactCurrency(max)}
                        </span>
                    </div>
                </div>

                <fieldset className='mt-11'>
                    <legend className='text-sm font-semibold text-primary-foreground/80'>
                        3. Prazo para pagar
                    </legend>

                    <div className='mt-4 flex flex-wrap gap-2'>
                        {category.terms.map(option => {
                            const isSelected = option === term

                            return (
                                <label
                                    key={option}
                                    className={cn(optionClassName, 'tabular-nums', isSelected ? optionSelectedClassName : optionIdleClassName)}
                                >
                                    <input
                                        type='radio'
                                        name={`${baseId}-prazo`}
                                        value={option}
                                        checked={isSelected}
                                        onChange={() => selectTerm(option)}
                                        className='sr-only'
                                    />

                                    {option} meses
                                </label>
                            )
                        })}
                    </div>
                </fieldset>

                <dl className='mt-11 grid gap-4 border-t border-ink-line pt-8 sm:grid-cols-3'>
                    <div>
                        <dt className='text-xs font-semibold uppercase tracking-[0.16em] text-primary-foreground/55'>
                            Juros
                        </dt>

                        <dd className='mt-1 font-heading text-3xl text-gold'>
                            Zero
                        </dd>
                    </div>

                    <div>
                        <dt className='text-xs font-semibold uppercase tracking-[0.16em] text-primary-foreground/55'>
                            Entrada
                        </dt>

                        <dd className='mt-1 font-heading text-3xl text-gold'>
                            Nenhuma
                        </dd>
                    </div>

                    <div>
                        <dt className='text-xs font-semibold uppercase tracking-[0.16em] text-primary-foreground/55'>
                            Contemplação
                        </dt>

                        <dd className='mt-1 font-heading text-3xl text-gold'>
                            Sorteio ou lance
                        </dd>
                    </div>
                </dl>
            </div>

            <div className='rounded-[1.75rem] bg-card p-6 text-card-foreground shadow-2xl shadow-black/30 sm:p-8'>
                <div className='flex items-start justify-between gap-4'>
                    <div>
                        <p className='text-sm font-semibold text-muted-foreground'>
                            Parcela estimada
                        </p>

                        <p className='mt-2 flex flex-wrap items-baseline gap-x-2'>
                            <span className='font-heading text-5xl leading-none tracking-tight tabular-nums sm:text-6xl'>
                                {formatCurrency(simulation.installment, { cents: true })}
                            </span>

                            <span className='text-base font-semibold text-muted-foreground'>
                                /mês
                            </span>
                        </p>

                        <p className='mt-3 text-sm text-muted-foreground'>
                            em {simulation.term} meses, sem juros e sem entrada
                        </p>
                    </div>

                    <span className='hidden size-12 shrink-0 place-items-center rounded-2xl bg-sand text-bronze sm:grid'>
                        <ConsortiumIcon slug={category.slug} className='size-6' />
                    </span>
                </div>

                <dl className='mt-7 divide-y divide-border border-y border-border text-sm'>
                    <BreakdownRow label='Carta de crédito' value={formatCurrency(simulation.credit)} />
                    <BreakdownRow label={`Taxa de administração (${formatPercent(category.adminFee)})`} value={formatCurrency(simulation.adminFeeValue)} />
                    <BreakdownRow label={`Fundo de reserva (${formatPercent(category.reserveFund)})`} value={formatCurrency(simulation.reserveFundValue)} />
                    <BreakdownRow label='Total ao fim do plano' value={formatCurrency(simulation.total)} emphasis />
                </dl>

                <div className='mt-7'>
                    <p className='text-sm font-semibold text-foreground'>
                        Total pago, comparado a um {category.financing.label}
                    </p>

                    <div className='mt-4 grid gap-4'>
                        <ComparisonBar
                            label='Consórcio'
                            detail={`parcela de ${formatCurrency(simulation.installment, { cents: true })}`}
                            value={simulation.total}
                            max={simulation.financingTotal}
                            fillClassName='bg-gold'
                        />

                        <ComparisonBar
                            label={`Financiamento a ${formatPercent(category.financing.monthlyRate)} ao mês`}
                            detail={`parcela de ${formatCurrency(simulation.financingInstallment, { cents: true })}`}
                            value={simulation.financingTotal}
                            max={simulation.financingTotal}
                            fillClassName='bg-primary'
                        />
                    </div>

                    <div className='mt-5 flex items-center justify-between gap-4 rounded-2xl bg-sand px-4 py-3'>
                        <p className='text-sm font-semibold text-foreground'>
                            Diferença estimada
                        </p>

                        <p className='font-heading text-2xl tabular-nums text-foreground sm:text-3xl'>
                            {formatCurrency(simulation.difference)}
                        </p>
                    </div>
                </div>

                <button
                    type='button'
                    onClick={() => onRequestProposal({ slug, credit, term })}
                    className={buttonClassName({ size: 'lg', className: 'mt-7 w-full' })}
                >
                    Quero receber esta proposta
                    <ArrowRight aria-hidden='true' className='size-5' />
                </button>

                <p className='mt-4 flex gap-2 text-xs leading-relaxed text-muted-foreground'>
                    <Info aria-hidden='true' className='mt-0.5 size-4 shrink-0' />

                    <span>
                        Simulação ilustrativa, sem seguro e sem o reajuste anual de parcelas e crédito ({category.adjustmentIndex}). Taxas e prazos variam conforme a administradora e o grupo. O financiamento considera a tabela Price, sem tarifas, seguros e IOF.
                    </span>
                </p>

                <p aria-live='polite' className='sr-only'>
                    {announcement}
                </p>
            </div>
        </div>
    )
}
